/**
 * EO «Финансы» — презентационные графики (без запросов; V1 index.html:7649-7691,
 * 7947-8010):
 *  • MiniBars — бары выручки по дням + будущие брони сверху; мердж
 *    dailyRevenue+futureByDate ПО ДАТАМ (futureByDate начинается строго после
 *    серверного today); горизонтальный скролл на длинном периоде.
 *  • BranchBars — двухсегментные полосы факт/прогноз по филиалам; мердж
 *    byBranch+estByBranch СТРОГО по branch_name — estByBranch приходит без
 *    branch_id (finance.ts:198, Object.values дропает ключ).
 *  • PayShare — доли способов оплаты (нал/терминал/перевод/предоплата/счёт; архив 1С, 16.10 — «Прочее»
 *    V4 и «Прочее (1С)»; модель и проценты — payShare.ts).
 * Высоты/ширины баров — inline (числа, не цвета); цвета только классами.
 */
import type {
  FinBranchRow, FinCompleted, FinDailyPoint, FinEstBranchRow, FinFuturePoint,
} from "../../hooks/useFinanceAnalytics";
import css from "./finance.module.css";
import { payShare, type PayKey } from "./payShare";

const rub = (n: number) => `₽${Math.round(n || 0).toLocaleString("ru-RU")}`;

export function MiniBars({ daily, future }: { daily: FinDailyPoint[]; future: FinFuturePoint[] }) {
  const map = new Map<string, { rev: number; games: number; fut: number }>();
  for (const p of daily) map.set(p.date, { rev: p.revenue, games: p.games, fut: 0 });
  for (const p of future) {
    const cur = map.get(p.date) ?? { rev: 0, games: 0, fut: 0 };
    cur.fut = p.count;
    map.set(p.date, cur);
  }
  const dates = [...map.keys()].sort();
  if (dates.length === 0) return <div className={css.chartEmpty}>Нет данных за период</div>;
  const maxRev = Math.max(...dates.map((d) => map.get(d)!.rev), 1);
  const maxFut = Math.max(...dates.map((d) => map.get(d)!.fut), 1);
  return (
    <div className={css.chartScroll}>
      <div className={css.chartRow}>
        {dates.map((date) => {
          const { rev, games, fut } = map.get(date)!;
          const rH = rev > 0 ? Math.max(3, Math.round((rev / maxRev) * 56)) : 0;
          const fH = fut > 0 ? Math.max(2, Math.round((fut / maxFut) * 24)) : 0;
          const dd = date.slice(8);
          const showDay = Number(dd) % 5 === 0 || dates.length <= 10;
          return (
            <div key={date} className={css.chartCol}>
              {fut > 0 && <div className={css.barFut} style={{ height: fH }} title={`${date}: ${fut} буд.`} />}
              {rev > 0 && <div className={css.barRev} style={{ height: rH }} title={`${date}: ${rub(rev)} · ${games} игр`} />}
              <span className={css.chartDay}>{showDay ? dd : " "}</span>
            </div>
          );
        })}
      </div>
      <div className={css.legend}>
        <span className={css.legendItem}><i className={css.swRev} /> Выручка</span>
        <span className={css.legendItem}><i className={css.swFut} /> Буд. брони</span>
      </div>
    </div>
  );
}

export function BranchBars({ byBranch, estByBranch }: { byBranch: FinBranchRow[]; estByBranch: FinEstBranchRow[] }) {
  const map = new Map<string, { rev: number; games: number; est: number; estCnt: number }>();
  for (const b of byBranch) map.set(b.branchName, { rev: b.revenue, games: b.gamesDone, est: 0, estCnt: 0 });
  for (const e of estByBranch) {
    const cur = map.get(e.branchName) ?? { rev: 0, games: 0, est: 0, estCnt: 0 };
    cur.est = e.est;
    cur.estCnt = e.count;
    map.set(e.branchName, cur);
  }
  const list = [...map.entries()].sort((a, b) => b[1].rev + b[1].est - (a[1].rev + a[1].est));
  if (list.length === 0) return <div className={css.chartEmpty}>Нет данных по филиалам</div>;
  const maxV = Math.max(...list.map(([, v]) => v.rev + v.est), 1);
  return (
    <>
      {list.map(([name, v]) => (
        <div key={name} className={css.brRow}>
          <div className={css.brHead}>
            <span className={css.brName}>{name}</span>
            <span className={css.brSum}>
              {rub(v.rev)}{v.est > 0 && <span className={css.brEst}> +{rub(v.est)}</span>}
            </span>
          </div>
          <div className={css.brTrack}>
            <div className={css.brSegRev} style={{ width: `${(v.rev / maxV) * 100}%` }} />
            <div className={css.brSegEst} style={{ width: `${(v.est / maxV) * 100}%` }} />
          </div>
          <div className={css.brSub}>
            {v.games > 0 && <span>{v.games} отведено</span>}
            {v.games > 0 && v.estCnt > 0 && " · "}
            {v.estCnt > 0 && <span>{v.estCnt} буд.</span>}
          </div>
        </div>
      ))}
    </>
  );
}

/* Цвета сегментов — палитра экрана «Финансы»; «Прочее (1С)» — приглушённый, как прочие пометки 1С (--text-2). */
const PAY_SW: Record<PayKey, string | undefined> = {
  cash: css.bgGold, terminal: css.bgBlue, transfer: css.bgPurple, prepaid: css.bgOrange, invoice: css.bgGreen,
  other: css.bgCyan, other1c: css.bgMuted,
};

export function PayShare({ completed }: { completed: FinCompleted }) {
  const { items } = payShare(completed);
  if (items.length === 0) return <div className={css.chartEmpty}>Оплат за период нет</div>;
  return (
    <>
      <div className={css.payBar}>
        {items.map((x) => <div key={x.key} className={`${css.paySeg} ${PAY_SW[x.key] ?? ""}`} style={{ flex: x.v }} />)}
      </div>
      <div className={css.payLegend}>
        {items.map((x) => (
          <span key={x.key} className={css.payItem}>
            <i className={`${css.paySw} ${PAY_SW[x.key] ?? ""}`} />
            {x.label} <span className={css.payVal}>{rub(x.v)}</span> ({x.pct}%)
          </span>
        ))}
      </div>
    </>
  );
}
